import {
    AccessMessageProps, MessageRights,
} from '../types'

import {
    HeroExclamationTriangleIconSolid, KeyIcon,
} from 'components/Icon'

import React, {useContext} from 'react'

import {
    Trans, useTranslation,
} from 'react-i18next'

import {
    buttonWrapper, channelInfoButton, channelName, invitationHighlighted, invitationWrapper,
    messageContainer, messageText, messageTime, realmName, speechBubble, speechBubbleContainer,
} from './styles'


import Button from 'components/Button'
import {EmbedContext} from 'components/EmbedContext'
import Highlight from 'components/Highlight'
import {InfoMessage} from 'components/InfoMessage'
import LetterAvatar from 'components/Avatar/LetterAvatar'
import {NinchatAccessFoundEvent} from 'api/types/events'
import UserAvatar from 'components/Avatar/UserAvatar'

import {
    nameText,
} from '../ChannelMessage/styles/ChannelMessage'
import {styled} from 'stitches/config'

import {
    useUiState,
} from 'hooks'

const StyledMessageContainer = styled('div', messageContainer)
const StyledSpeechBubbleContainer = styled('div', speechBubbleContainer)
const StyledSpeechBubble = styled('div', speechBubble)
const StyledTime = styled('time', messageTime)
const StyledName = styled('div', nameText)

const StyledChannelInfoButton = styled('button', channelInfoButton)
const StyledInvitationWrapper = styled('div', invitationWrapper)
const StyledRealmName = styled('div', realmName)
const StyledChannelName = styled('div', channelName)
const StyledInvitationHighlighted = styled('div', invitationHighlighted)
const StyledMessageText = styled('div', messageText)
const StyledButtonWrapper = styled('div', buttonWrapper)

export interface AccessMessageViewProps extends AccessMessageProps {
    channelAlreadyAdded: boolean
    channelDetails?: Partial<NinchatAccessFoundEvent> & {error?: boolean} | null
    showInvitationModal: () => void
    rights: MessageRights
}

const AccessMessageView = (props: AccessMessageViewProps) => {
    const {
        message, time, isMe, isRepeated, iconUrl, channelDetails, showInvitationModal,
        channelAlreadyAdded, rights,
    } = props

    const {t} = useTranslation()

    const name = message.message_user_name

    const ui = useUiState()

    const handleShowInvitation = () => {
        if (!channelAlreadyAdded) {
            showInvitationModal()
        }
    }

    const isAccessInfoMessage = message.message_type === 'ninchat.com/info/access'
    const repeated = isRepeated && !isAccessInfoMessage

    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'

    // eslint-disable-next-line no-extra-parens
    const showAvatar = !isEmbedQueueChannel || (isEmbedQueueChannel && (embed?.messageAvatars === 'all' || (embed?.messageAvatars === 'agentsOnly' && !isMe)))

    return (
        <StyledMessageContainer
            hidden={message.message_hidden}
            me={isMe}
            repeated={repeated}
        >
            {!repeated && !isAccessInfoMessage && !!showAvatar &&
                <UserAvatar
                    name={name}
                    size={ui?.chat_density === 'compact' ? 'small' : undefined}
                    src={iconUrl}
                />}
            <StyledSpeechBubbleContainer me={isMe} repeated={!!(repeated && showAvatar)}>
                {!repeated && <StyledName isEmbedQueueChannel={isEmbedQueueChannel} me={isMe} rights={rights}>{name}</StyledName>}
                <StyledSpeechBubble isEmbedQueueChannel={isEmbedQueueChannel} me={isMe} repeated={repeated} rights={rights}>
                    <StyledInvitationWrapper>
                        {message.text ?
                            <StyledMessageText>
                                <Highlight
                                    linkTarget={message.text.includes(window.location.host) ? '_self' : '_blank'}
                                >{String(message.text)}
                                </Highlight>
                            </StyledMessageText>
                            : null}
                        <StyledInvitationHighlighted>
                            {channelDetails?.error ?
                                <InfoMessage
                                    icon={HeroExclamationTriangleIconSolid}
                                    infoHeader={t('Problem with invitation')}
                                    message={t('Invitation link has expired or is erroneous.')}
                                    variant='error'
                                /> :
                                <div>
                                    <div>
                                        <Trans i18nKey='Invite To Channel'>
                                            <strong>{{
                                                name: channelDetails?.user_attrs?.name ?
                                                    channelDetails?.user_attrs?.name
                                                    : channelDetails?.user_attrs?.deleted ? t('Deleted user') : t('Unnamed user'),
                                            } as unknown as string}
                                            </strong> invited you to channel:
                                        </Trans>
                                    </div>
                                    <StyledChannelInfoButton onClick={handleShowInvitation}>
                                        <StyledChannelName>
                                            <LetterAvatar
                                                name={channelDetails?.channel_attrs?.name}
                                            />
                                            <div>
                                                <div><strong>{channelDetails?.channel_attrs?.name}</strong></div>
                                                <StyledRealmName>{channelDetails?.realm_attrs?.name}</StyledRealmName>
                                            </div>
                                        </StyledChannelName>
                                    </StyledChannelInfoButton>
                                    <div>
                                        {t('Invitations are valid for 14 days.')}
                                    </div>
                                </div> }
                        </StyledInvitationHighlighted>
                        <div>
                            <StyledButtonWrapper>
                                {!channelDetails?.error &&
                                    <Button
                                        color='filled'
                                        disabled={channelAlreadyAdded}
                                        iconLeft={KeyIcon}
                                        onPress={handleShowInvitation}
                                        text={channelAlreadyAdded ? t('Already a member') : t('Show invitation')}
                                    />}
                            </StyledButtonWrapper>
                            <StyledTime isEmbedQueueChannel={isEmbedQueueChannel} rights={rights}>{time}</StyledTime>
                        </div>
                    </StyledInvitationWrapper>
                </StyledSpeechBubble>
            </StyledSpeechBubbleContainer>
        </StyledMessageContainer>
    )
}

AccessMessageView.defaultProps = {channelDetails: undefined}

export default AccessMessageView
